import { ErrorModal, getMomentTz } from "@dexteel/mesf-core";
import { Box, Button, Grid2, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { Part, PassesCampaignData } from "../models/Part";
import { LogsModal } from "./log-modal/LogsModal";
import { PartCampaignChart } from "./part-campaigns/PartCampaignChart";
import { ResetPartCampaignModal } from "./part-campaigns/ResetPartCampaignModal";
import { UpdatePartCampaignModal } from "./part-campaigns/UpdatePartCampaignModal";
import { SpecificCassetteChockData } from "./parts-panels/SpecificCassetteChockData";
import {
	SpecificArborData,
	SpecificCarrierData,
	SpecificCassetteData,
	SpecificChockData,
	SpecificDonutData,
	SpecificEdgerRollData,
	SpecificGuideData,
	SpecificUniversalRollData,
} from "./parts-panels/specifics.datas";
import { partTypeCodes } from "./TableParts";
import { DatePicker } from "@mui/x-date-pickers";

const moment = getMomentTz();

type Props = {
	part: Part | null;
	passes: PassesCampaignData[];
	showDetails: keyof typeof components;
};

const components = {
	A: SpecificArborData,
	H: SpecificCarrierData,
	T: SpecificCassetteData,
	C: SpecificChockData,
	D: SpecificDonutData,
	E: SpecificEdgerRollData,
	G: SpecificGuideData,
	U: SpecificUniversalRollData,
	K: SpecificCassetteChockData,
	"": null,
};

export const PartDetails = ({ part, passes, showDetails }: Props) => {
	const hideTag = part?.PartTypeCode === "D";

	const [error, setError] = useState("");
	const [showModal, setShowModal] = useState<"update" | "reset" | "">("");

	const SpecificComponent = components[showDetails];

	return (
		<>
			<Box
				sx={{
					marginBottom: (theme) => theme.spacing(1),
					padding: (theme) => theme.spacing(2),
					backgroundColor: (theme) => theme.palette.grey[50],
					borderRadius: (theme) => theme.spacing(1),
					border: (theme) => `1px solid ${theme.palette.grey[300]}`,
				}}
			>
				<Typography variant="h6" marginBottom={(theme) => theme.spacing(2)}>
					Part Details
				</Typography>
				<Grid2 container spacing={1}>
					<Grid2 size={{ xs: 12, sm: 12, md: 2 }}>
						<TextField
							value={
								partTypeCodes.find((t) => t.Code === part?.PartTypeCode)
									?.Name || ""
							}
							label="Type"
							variant="outlined"
							size="small"
							fullWidth
							disabled
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, sm: 12, md: !hideTag ? 2 : 4 }}>
						<TextField
							value={part?.PartName || ""}
							label="Name"
							variant="outlined"
							size="small"
							fullWidth
							disabled
						/>
					</Grid2>
					{!hideTag && (
						<Grid2 size={{ xs: 12, sm: 12, md: 2 }}>
							<TextField
								value={part?.RFIDTag || ""}
								label="Tag"
								variant="outlined"
								size="small"
								fullWidth
								disabled
							/>
						</Grid2>
					)}
					<Grid2 size={{ xs: 12, sm: 12, md: 6 }}>
						<TextField
							value={part?.LocationName || ""}
							label="Location"
							variant="outlined"
							size="small"
							fullWidth
							disabled
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, sm: 12, md: 12 }}>
						{SpecificComponent && <SpecificComponent part={part} />}
					</Grid2>
					<Grid2 size={{ xs: 12, sm: 12, md: 12 }}>
						<LogsModal partId={part?.PartId!} partName={part?.PartName ?? ""} />
					</Grid2>
				</Grid2>
			</Box>

			<Box
				sx={{
					marginBottom: (theme) => theme.spacing(1),
					padding: (theme) => theme.spacing(2),
					backgroundColor: (theme) => theme.palette.grey[50],
					borderRadius: (theme) => theme.spacing(1),
					border: (theme) => `1px solid ${theme.palette.grey[300]}`,
				}}
			>
				<Typography variant="h6" marginBottom={(theme) => theme.spacing(2)}>
					Part Campaign
				</Typography>
				<Grid2
					container
					spacing={1}
					justifyContent="center"
					alignItems="center"
				>
					<Grid2 size={{ md: 3 }}>
						<DatePicker
							disabled
							label="Last Reset"
							format="MM/DD/YYYY HH:mm"
							value={
								part?.LastResetTimestamp
									? moment(part?.LastResetTimestamp)
									: null
							}
							slotProps={{
								textField: {
									size: "small",
									error: !!error,
								},
							}}
						/>
					</Grid2>
					<Grid2 size={{ md: 3 }}>
						<TextField
							value={
								parseFloat(
									(
										(part?.CampaignWeight ?? 0) +
										(part?.OffsetWeight ?? 0) +
										(part?.StartWeight ?? 0)
									)?.toFixed(2),
								) || "0"
							}
							label="Accumulated Tons"
							variant="outlined"
							size="small"
							fullWidth
							disabled
						/>
					</Grid2>
					<Grid2 size={{ md: 3 }}>
						<Button
							variant="contained"
							color="primary"
							onClick={() => {
								setShowModal("update");
							}}
							size="medium"
							fullWidth
							disabled
						>
							UPDATE COUNTER
						</Button>
					</Grid2>
					<Grid2 size={{ md: 3 }}>
						<Button
							variant="outlined"
							color="primary"
							onClick={() => {
								setShowModal("reset");
							}}
							size="medium"
							fullWidth
						>
							RESET COUNTER
						</Button>
					</Grid2>
					{passes.map((pass) => (
						<Grid2 key={pass.PassNumber} size={{ md: 12 / passes.length }}>
							<TextField
								value={parseFloat(pass.TotalWeight?.toFixed(2))}
								label={`Pass ${pass.PassNumber} Acc. Tons`}
								variant="outlined"
								size="small"
								fullWidth
								disabled
							/>
						</Grid2>
					))}

					<Grid2 size={{ md: 12 }} style={{ marginTop: 10 }}>
						<PartCampaignChart partId={part?.PartId!} />
					</Grid2>
					<Typography
						variant="h6"
						style={{ fontSize: "0.7rem" }}
						color="textSecondary"
						align="center"
					>
						Click on a date to see it in the gantt
					</Typography>
				</Grid2>
			</Box>
			<ErrorModal error={error} onHide={() => setError("")} />
			<UpdatePartCampaignModal
				open={showModal === "update"}
				onHide={() => setShowModal("")}
				partId={part?.PartId!}
				partCampaignId={part?.PartCampaignId!}
				info={{
					partName: part?.PartName,
					partType: partTypeCodes.find((p) => p.Code === part?.PartTypeCode)
						?.Name,
					campaignWeight: part?.CampaignWeight ?? 0,
					offsetWeight: part?.OffsetWeight ?? 0,
				}}
			/>
			<ResetPartCampaignModal
				open={showModal === "reset"}
				onHide={() => setShowModal("")}
				partId={part?.PartId!}
				info={{
					partName: part?.PartName,
					partType: partTypeCodes.find((p) => p.Code === part?.PartTypeCode)
						?.Name,
					campaignWeight: part?.CampaignWeight ?? 0,
				}}
			/>
		</>
	);
};
